import { useEffect, useRef, useState } from "preact/hooks";
import { ComponentChildren } from "preact";

const pageNames = [
  "Unified Communications",
  "Customer Experience",
  "Network Infrastructure",
  "Cyber Security",
  "Managed Services & Support",
  "Artificial Intelligence",
  "Carrier Services",
];

const pagePaths = [
  `/services/unified-communications`,
  `/services/customer-experience`,
  `/services/network-infrastructure`,
  `/services/cyber-security`,
  `/services/managed-services-support`,
  `/services/ai`,
  `/services/carrier-services`,
];

export function HomeSlider(props: { children: ComponentChildren }) {
  const [activeIndex, setActiveIndex] = useState(0);
  const contentRef = useRef<HTMLDivElement>(null);
  const buttonsRef = useRef<HTMLDivElement>(null);
  const observerRef = useRef<IntersectionObserver>();
  const lineRef = useRef<HTMLDivElement>(null);

  function setDefaultSection() {
    const url = new URL(globalThis.location.href);
    const page = url.pathname;
    const pageIndex = page ? pagePaths.indexOf(page) : 0;

    if (!contentRef.current) return;

    const section = (contentRef.current.children[pageIndex] ??
      contentRef.current.children[0]) as HTMLElement;
    contentRef.current.parentElement?.scrollTo({ left: section.offsetLeft });
  }

  useEffect(() => {
    setDefaultSection();
  }, []);

  // Set up intersection observer for scroll detection
  useEffect(() => {
    if (!contentRef.current) return;

    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const index = Array.from(contentRef.current!.children).indexOf(
              entry.target,
            );
            setActiveIndex(index);
          }
        });
      },
      {
        root: contentRef.current.parentElement,
        threshold: 0.5,
      },
    );

    Array.from(contentRef.current.children).forEach((child) => {
      observerRef.current?.observe(child);
    });

    return () => observerRef.current?.disconnect();
  }, []);

  // Update indicator position
  useEffect(() => {
    if (!buttonsRef.current || !lineRef.current) return;

    const activeButton = buttonsRef.current
      .children[activeIndex + 1] as HTMLElement;
    if (!activeButton) return;

    lineRef.current.style.width = `${activeButton.offsetWidth}px`;
    lineRef.current.style.transform =
      `translateX(${activeButton.offsetLeft}px)`;
  }, [activeIndex]);

  // Handle button clicks
  const scrollToSection = (index: number, smooth = true) => {
    if (!contentRef.current) return;

    const section = contentRef.current.children[index] as HTMLElement;
    contentRef.current.parentElement?.scrollTo({
      left: section.offsetLeft,
      behavior: smooth ? "smooth" : "auto",
    });
  };

  return (
    <div class="home-slider">

      <div class="buttons" ref={buttonsRef}>
        <div class="indicator" ref={lineRef} />
        {pageNames.map((label, index) => (
          <button
            key={index}
            onClick={() => scrollToSection(index)}
            class={activeIndex === index ? "active" : ""}
          >
            <img
              src={`/img/icons/homeicon-${label.toLowerCase().replace(/ /g, "")
                }.svg`}
              alt={`${label} icon`}
            />
            {label}
          </button>
        ))}
      </div>

      <div class="scroll">
        <div class="content" ref={contentRef}>
          <div>
            <p>
              Collaboration should be effortless, no matter where teams work.
              Gage integrates industry-leading tools like Avaya, Microsoft Teams, Zoom,
              and Cisco Webex into seamless, scalable solutions, delivering
              superior reliability and a frustration-free user experience that
              boosts productivity.
            </p>
            <a href="/services/unified-communications">
              Explore Unified Communications
            </a>
          </div>
          <div>
            <p>
              Customers expect seamless, personalized interactions. Gage makes it
              happen. Our AI-powered CX platforms, omnichannel solutions, and
              workforce optimization tools help businesses engage smarter,
              resolve issues faster, and build lasting loyalty.
            </p>
            <a href="/services/customer-experience">
              Explore Customer Experience
            </a>
          </div>
          <div>
            <p>
              A strong business starts with a strong network. Gage architects
              secure, scalable networks using Aruba, Cisco, and Juniper
              solutions, ensuring fast, reliable connectivity that adapts to
              future demands and eliminates costly downtime.
            </p>
            <a href="/services/network-infrastructure?notab=1">
              Explore Network Infrastructure
            </a>
          </div>
          <div>
            <p>
              Today’s cyber threats require proactive defense. Gage delivers
              enterprise-grade security solutions, including endpoint
              protection, firewalls, and threat intelligence, leveraging
              CrowdStrike, Fortinet, and Palo Alto to keep your data, systems,
              and reputation safe.
            </p>
            <a href="/services/cyber-security">
              Explore Cyber Security
            </a>
          </div>
          <div>
            <p>
              Technology should work for you, not against you. Gage’s 24/7
              monitoring, disaster recovery, and cloud management services
              proactively prevent issues before they impact your
              business, reducing downtime and IT headaches.
            </p>
            <a href="/services/managed-services-support">
              Explore Managed Services & Support
            </a>
          </div>
          <div>
            <p>
              AI is only worth deploying if it's secure, governed, and tied
              to a business outcome. Gage starts with an AI Readiness
              Assessment, then integrates AI into your contact center,
              collaboration, and data platforms with guardrails from day one.
            </p>
            <a href="/services/ai">
              Explore Artificial Intelligence
            </a>
          </div>
          <div>
            <p>
              Reliable connectivity is the backbone of every digital business.
              Gage simplifies carrier service selection and management, ensuring
              high-performance, cost-effective solutions that keep businesses
              connected without interruptions.
            </p>
            <a href="/services/carrier-services">
              Explore Carrier Services
            </a>
          </div>
        </div>
      </div>

    </div>
  );
}
